import React, { useEffect, useMemo, useState } from 'react';
import { Users, X } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { confirmParticipants, acknowledgeParticipants } from '../store/conversationSlice';
import { getSpeakerColor } from '../config/participants';
import { recallWebSocketService } from '../services/recallWebSocketService';

type Role = 'salesperson' | 'customer';
interface RowEdit {
  role: Role;
  name: string;
}

interface ParticipantRolesModalProps {
  /** Only active in recall bot mode (a bot is selected). */
  enabled: boolean;
}

/**
 * Real-time participant roster confirmation for bot-mode calls. As participants
 * join (tracked from recall join events), this auto-opens so the salesperson can
 * confirm who is the salesperson and name each customer. Assignments propagate to
 * the backend (update_participants) which backfills prior turns via the saver.
 */
const ParticipantRolesModal: React.FC<ParticipantRolesModalProps> = ({ enabled }) => {
  const dispatch = useAppDispatch();
  const participants = useAppSelector((s) => s.conversation.participants);
  const confirmedIds = useAppSelector((s) => s.conversation.confirmedParticipantIds);

  const [isOpen, setIsOpen] = useState(false);
  const [edits, setEdits] = useState<Record<string, RowEdit>>({});

  const rosterKey = participants.map((p) => p.participantId).join(',');
  const hasUnconfirmed = useMemo(
    () => participants.some((p) => p.participantId && !confirmedIds.includes(p.participantId)),
    [participants, confirmedIds]
  );

  // Auto-open every time a new (unconfirmed) participant appears.
  useEffect(() => {
    if (enabled && hasUnconfirmed) setIsOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, rosterKey]);

  // Seed local edits from the roster whenever the modal is open or the roster grows.
  useEffect(() => {
    if (!isOpen) return;
    setEdits((prev) => {
      const next: Record<string, RowEdit> = { ...prev };
      participants.forEach((p, idx) => {
        if (!p.participantId) return;
        if (!next[p.participantId]) {
          next[p.participantId] = {
            role: (p.role as Role) || 'customer',
            name: p.name || (p.role === 'salesperson' ? '' : `Customer ${idx + 1}`),
          };
        }
      });
      return next;
    });
  }, [isOpen, rosterKey, participants]);

  if (!enabled || !isOpen || participants.length === 0) return null;

  const setRole = (participantId: string, role: Role) => {
    setEdits((prev) => {
      const next = { ...prev };
      // Exactly one salesperson: demote others when one is chosen.
      if (role === 'salesperson') {
        Object.keys(next).forEach((id) => {
          if (id !== participantId && next[id].role === 'salesperson') {
            next[id] = { ...next[id], role: 'customer' };
          }
        });
      }
      next[participantId] = { ...next[participantId], role };
      return next;
    });
  };

  const setName = (participantId: string, name: string) => {
    setEdits((prev) => ({ ...prev, [participantId]: { ...prev[participantId], name } }));
  };

  const salespersonCount = Object.values(edits).filter((e) => e.role === 'salesperson').length;
  const canConfirm = salespersonCount === 1;

  const buildAssignments = () =>
    participants
      .filter((p) => p.participantId)
      .map((p) => {
        const e = edits[p.participantId] || { role: (p.role as Role) || 'customer', name: p.name || '' };
        return {
          participantId: p.participantId,
          role: e.role,
          name: e.role === 'customer' ? (e.name || '').trim() || undefined : undefined,
        };
      });

  const handleConfirm = () => {
    if (!canConfirm) return;
    const assignments = buildAssignments();
    dispatch(confirmParticipants(assignments));
    recallWebSocketService.updateParticipants(assignments);
    setIsOpen(false);
  };

  const handleDismiss = () => {
    // Acknowledge current roster so it doesn't immediately re-open; best-guess
    // roles stay in effect until the salesperson revisits.
    dispatch(acknowledgeParticipants());
    setIsOpen(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-blue-600" />
            <h2 className="text-sm font-semibold text-gray-800">
              Who is on the call?
            </h2>
          </div>
          <button onClick={handleDismiss} className="text-gray-400 hover:text-gray-600" aria-label="Dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-3">
          <p className="text-xs text-gray-500 mb-3">
            Confirm the salesperson and name each customer. This applies to the whole
            call and updates as new people join.
          </p>

          <div className="space-y-2 max-h-80 overflow-y-auto">
            {participants.map((p, idx) => {
              const e = edits[p.participantId] || { role: (p.role as Role) || 'customer', name: p.name || '' };
              const color = getSpeakerColor({ speaker: e.role, participantId: p.participantId }, participants as any);
              return (
                <div
                  key={p.participantId || idx}
                  className="flex items-center gap-2 border border-gray-100 rounded-lg px-2 py-1.5"
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: color.bg, border: `1px solid ${color.border}` }}
                  />
                  <span className="text-xs text-gray-500 w-28 truncate" title={p.name}>
                    {p.name || `Participant ${idx + 1}`}
                  </span>
                  <select
                    value={e.role}
                    onChange={(ev) => setRole(p.participantId, ev.target.value as Role)}
                    className="text-xs border border-gray-300 rounded px-1.5 py-1 bg-white focus:ring-2 focus:ring-blue-400 outline-none"
                  >
                    <option value="salesperson">Salesperson</option>
                    <option value="customer">Customer</option>
                  </select>
                  <input
                    value={e.role === 'customer' ? e.name : ''}
                    onChange={(ev) => setName(p.participantId, ev.target.value)}
                    disabled={e.role === 'salesperson'}
                    placeholder={e.role === 'salesperson' ? 'You' : 'Customer name'}
                    className="flex-1 text-xs border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-400 outline-none disabled:bg-gray-50 disabled:text-gray-400"
                  />
                </div>
              );
            })}
          </div>

          {!canConfirm && (
            <p className="text-xs text-red-600 mt-2">
              {salespersonCount === 0
                ? 'Mark exactly one participant as the salesperson.'
                : 'Only one participant can be the salesperson.'}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200">
          <button
            onClick={handleDismiss}
            className="text-xs px-3 py-1.5 text-gray-600 hover:text-gray-800"
          >
            Not now
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="text-xs px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Confirm roles
          </button>
        </div>
      </div>
    </div>
  );
};

export default ParticipantRolesModal;
